import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import data from "../../data/index.json";
import "./Testimonials.css";

export default function Testimonial() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.6, 
        ease: [0.25, 0.46, 0.45, 0.94] 
      } 
    },
  };

  const downloadCertificate = (id, name) => {
    const url = process.env.PUBLIC_URL + `/img/cr${id}.pdf`;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${name.replace(/\s+/g, '_')}_certificate.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCertificateColor = (index) => {
    const colors = [
      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
    ];
    return colors[index % colors.length];
  };

  return (
    <section 
      ref={sectionRef}
      className="certifications-section" 
      id="testimonial"
      aria-labelledby="certifications-heading"
    >
      <div className="certifications-container">
        {/* Enhanced Header */}
        <motion.div 
          className="certifications-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <motion.p 
            className="section-subtitle"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Academic Journey
          </motion.p>
          <motion.h2 
            id="certifications-heading" 
            className="section-heading"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Education & Certifications
          </motion.h2>
          <motion.p 
            className="section-description"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Formal education and professional certifications that validate my expertise 
            in full-stack development and software engineering.
          </motion.p>
        </motion.div>

        {/* Enhanced Grid */}
        <motion.div
          className="certifications-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {Array.isArray(data?.testimonial) && data.testimonial.length ? (
            data.testimonial.map((item, index) => (
              <motion.article
                key={index}
                className="certification-card"
                variants={cardVariants}
                whileHover={{ 
                  y: -12,
                  scale: 1.02,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                whileTap={{ scale: 0.98 }}
                aria-labelledby={`cert-${index}`}
              >
                {/* Certificate Badge */}
                <div className="certificate-badge" aria-hidden="true">
                  <div 
                    className="badge-background"
                    style={{ background: getCertificateColor(index) }}
                  />
                  <div className="badge-icon">
                    <img
                      src={item.image ?? "https://www.svgrepo.com/show/263172/diploma-certificate.svg"}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "data:image/svg+xml;charset=utf-8," +
                          encodeURIComponent(
                            `<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'><defs><linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='100%'><stop offset='0%' stop-color='%23667eea'/><stop offset='100%' stop-color='%23764ba2'/></linearGradient></defs><rect width='64' height='64' rx='32' fill='url(%23grad)'/><path d='M24 32L28 36L40 24' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' fill='none'/></svg>`
                          );
                      }}
                    />
                  </div>
                  <div className="badge-ribbon" />
                </div>

                {/* Card Content */}
                <div className="certification-content">
                  <h3 id={`cert-${index}`} className="certification-title">
                    {item.name}
                  </h3>
                  
                  <div className="certification-meta">
                    <div className="meta-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{item.date}</span>
                    </div>
                    <div className="meta-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 14L9 11M12 14L15 11M12 14V7M5 14H19C20.1046 14 21 14.8954 21 16V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V16C3 14.8954 3.89543 14 5 14Z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{item.school}</span>
                    </div>
                  </div>

                  <p className="certification-description">
                    {item.description}
                  </p>

                  <motion.div 
                    className="certification-actions"
                    whileHover="hover"
                  >
                    <motion.button
                      type="button"
                      className="btn-download"
                      onClick={() => downloadCertificate(item.id, item.name)}
                      aria-label={`Download ${item.name} certificate`}
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <span>Download Certificate</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 3v12m0 0l-4-4m4 4l4-4m2 5h3M3 21h18" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.button>
                  </motion.div>
                </div>

                {/* Decorative Corner */}
                <div className="card-corner" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20V20H4V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </motion.article>
            ))
          ) : (
            <motion.div 
              className="certifications-empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="empty-state">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 14L9 11M12 14L15 11M12 14V7M5 14H19C20.1046 14 21 14.8954 21 16V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V16C3 14.8954 3.89543 14 5 14Z" 
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>No Certifications Available</h3>
                <p>Check back soon for updates on my educational journey</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}